import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { Slider } from '../components/ui/slider';
import { toast } from 'sonner';
import axios from 'axios';
import AthleteContextHeader from '../components/AthleteContextHeader';
import SearchableAthleteSelect from '../components/SearchableAthleteSelect';
import { CONTRACT_FIXTURE_ATHLETE_IDS, shouldLimitForContractFixture, getArchivedInjuryIds, archiveInjuryFromActiveView, unarchiveInjuryFromActiveView } from '../lib/visibility';
import { Trash2, Pencil, StickyNote, Archive, ArchiveRestore } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InjuryRecoveryPage = ({ athleteId: propAthleteId }) => {
  const { user } = useAuth();
  const location = useLocation();
  const isStaff = user?.role === 'Admin' || user?.role === 'Staff';
  const isScopedMode = !!propAthleteId;
  // Phase 74 — accept ?athlete_id=<id> query param so the Athlete 360 Health card
  // can deep-link into the main Injury & Recovery page already filtered.
  const queryAthleteId = React.useMemo(() => {
    try { return new URLSearchParams(location.search).get('athlete_id') || ''; }
    catch { return ''; }
  }, [location.search]);
  
  // State
  const [athletes, setAthletes] = useState([]);
  // Phase 74 — initial selection prefers ?athlete_id= query param (used for
  // deep-links from Athlete 360 Health card). Falls back to '' otherwise.
  const _initialQueryId = (() => {
    try { return new URLSearchParams(location.search).get('athlete_id') || ''; }
    catch { return ''; }
  })();
  const [selectedAthlete, setSelectedAthlete] = useState(_initialQueryId);
  const [injuries, setInjuries] = useState([]);
  const [selectedInjury, setSelectedInjury] = useState(null);
  const [injuryDetails, setInjuryDetails] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [access, setAccess] = useState({ can_view: false, can_edit: false, can_edit_medical: false });
  
  // Dialog states
  const [createInjuryDialog, setCreateInjuryDialog] = useState(false);
  const [createPlanDialog, setCreatePlanDialog] = useState(false);
  const [progressDialog, setProgressDialog] = useState(false);
  const [clearanceDialog, setClearanceDialog] = useState(false);
  
  // Form states
  const [newInjury, setNewInjury] = useState({
    injury_type: '', injury_category: 'Muscle', injury_category_other: '',
    body_area: '', severity: 'Unknown', date_of_injury: '',
    expected_recovery_days: '', diagnosis_notes: '', cause: 'Unknown'
  });
  const [newPlan, setNewPlan] = useState({
    phases: [
      { phase_name: 'Phase 1 - Rest', exercises: '', restrictions: '', weekly_goals: '', completed: false },
      { phase_name: 'Phase 2 - Rehabilitation', exercises: '', restrictions: '', weekly_goals: '', completed: false },
      { phase_name: 'Phase 3 - Conditioning', exercises: '', restrictions: '', weekly_goals: '', completed: false },
      { phase_name: 'Phase 4 - Return to Play', exercises: '', restrictions: '', weekly_goals: '', completed: false }
    ],
    overall_notes: '', target_return_date: ''
  });
  const [newProgress, setNewProgress] = useState({
    recovery_percent: 0, pain_level: 5, mobility_rating: 5, fitness_readiness: 5, notes: ''
  });
  const [newClearance, setNewClearance] = useState({
    clearance_status: 'Not Cleared', notes: '', restrictions: ''
  });

  // Internal notes state
  const [injuryNotes, setInjuryNotes] = useState([]);
  const [newNoteContent, setNewNoteContent] = useState('');

  // Active Injuries Board (operational view across athletes)
  const [boardRows, setBoardRows] = useState([]);
  const [boardLoading, setBoardLoading] = useState(false);
  const [boardExpanded, setBoardExpanded] = useState(!propAthleteId);

  // "Archive from Active View" — UI-only, localStorage-backed. Never touches
  // backend data; archived ids are filtered from this board, while every
  // other view (Athlete 360, history, analytics) stays untouched.
  const [archivedIds, setArchivedIds] = useState(() => getArchivedInjuryIds());
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    const onChange = () => setArchivedIds(getArchivedInjuryIds());
    window.addEventListener('pfap:injury-archive-change', onChange);
    window.addEventListener('storage', onChange);
    return () => {
      window.removeEventListener('pfap:injury-archive-change', onChange);
      window.removeEventListener('storage', onChange);
    };
  }, []);

  const visibleBoardRows = boardRows.filter((row) =>
    showArchived ? true : !archivedIds.has(row.injury_id)
  );
  const archivedCount = boardRows.filter((row) => archivedIds.has(row.injury_id)).length;

  const handleArchiveRow = (injuryId) => {
    archiveInjuryFromActiveView(injuryId);
    setArchivedIds(getArchivedInjuryIds());
    toast.success('Archived from Active View — record still available elsewhere.');
  };

  const handleUnarchiveRow = (injuryId) => {
    unarchiveInjuryFromActiveView(injuryId);
    setArchivedIds(getArchivedInjuryIds());
    toast.success('Restored to Active View.');
  };

  // Quick clearance mini-dialog
  const [quickClearDialog, setQuickClearDialog] = useState(null); // { injury_id, athlete_name, existing_status }
  const [quickClearStatus, setQuickClearStatus] = useState('Cleared');
  const [quickClearRestrictions, setQuickClearRestrictions] = useState('');

  // Quick progress mini-dialog (used from the board)
  const [quickProgressDialog, setQuickProgressDialog] = useState(null); // { injury_id, athlete_id, athlete_name }
  const [quickProgressPct, setQuickProgressPct] = useState(0);
  const [quickProgressNote, setQuickProgressNote] = useState('');

  // Recovery plan templates
  const [templates, setTemplates] = useState([]);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templateNameInput, setTemplateNameInput] = useState('');

  // "Quick Report Injury" entry point — lets physio open the create dialog
  // without first choosing an athlete from the outer selector.
  const [quickInjuryAthlete, setQuickInjuryAthlete] = useState('');

  const fetchInjuryNotes = useCallback(async (injuryId) => {
    if (!injuryId) { setInjuryNotes([]); return; }
    try {
      const res = await axios.get(`${API}/injury-recovery/injuries/${injuryId}/notes`);
      setInjuryNotes(res.data || []);
    } catch { setInjuryNotes([]); }
  }, []);

  const fetchActiveBoard = useCallback(async () => {
    if (!isStaff) return;
    setBoardLoading(true);
    try {
      const res = await axios.get(`${API}/injury-recovery/active-injuries`);
      // Defensive local filter. Interceptor already filters this endpoint via
      // the visibility registry; this line is a backup if the interceptor is
      // bypassed for any reason (e.g. during a future refactor).
      const rows = (res.data || []).filter(r => !CONTRACT_FIXTURE_ATHLETE_IDS.has(r.athlete?.id));
      setBoardRows(rows);
    } catch { setBoardRows([]); }
    finally { setBoardLoading(false); }
  }, [isStaff]);

  const fetchTemplates = useCallback(async () => {
    if (!isStaff) return;
    try {
      const res = await axios.get(`${API}/injury-recovery/recovery-templates`);
      setTemplates(res.data || []);
    } catch { setTemplates([]); }
  }, [isStaff]);

  const handleQuickClear = async () => {
    if (!quickClearDialog) return;
    try {
      await axios.post(`${API}/injury-recovery/clearance`, {
        injury_id: quickClearDialog.injury_id,
        athlete_id: quickClearDialog.athlete_id,
        clearance_status: quickClearStatus,
        restrictions: quickClearRestrictions || '',
        notes: '',
      });
      toast.success(
        quickClearStatus === 'Cleared'
          ? `${quickClearDialog.athlete_name} cleared to play`
          : 'Clearance updated'
      );
      setQuickClearDialog(null);
      setQuickClearRestrictions('');
      fetchActiveBoard();
    } catch (e) {
      // Clearance may already exist — fall back to PUT via the existing
      // record found in latest list_active.
      try {
        const existing = await axios.get(
          `${API}/injury-recovery/clearance/injury/${quickClearDialog.injury_id}`
        );
        if (existing.data?.id) {
          await axios.put(`${API}/injury-recovery/clearance/${existing.data.id}`, {
            clearance_status: quickClearStatus,
            restrictions: quickClearRestrictions || '',
            notes: '',
          });
          toast.success(
            quickClearStatus === 'Cleared'
              ? `${quickClearDialog.athlete_name} cleared to play`
              : 'Clearance updated'
          );
          setQuickClearDialog(null);
          fetchActiveBoard();
          return;
        }
      } catch { /* noop */ }
      toast.error(e?.response?.data?.detail || 'Failed to update clearance');
    }
  };

  const handleQuickProgress = async () => {
    if (!quickProgressDialog) return;
    try {
      await axios.post(`${API}/injury-recovery/progress`, {
        injury_id: quickProgressDialog.injury_id,
        athlete_id: quickProgressDialog.athlete_id,
        recovery_percent: quickProgressPct,
        pain_level: 5,
        mobility_rating: 5,
        fitness_readiness: 5,
        notes: quickProgressNote || '',
      });
      toast.success(`${quickProgressDialog.athlete_name}: progress recorded`);
      setQuickProgressDialog(null);
      setQuickProgressNote('');
      setQuickProgressPct(0);
      fetchActiveBoard();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to record progress');
    }
  };

  const handleApplyTemplate = (templateId) => {
    const t = templates.find(x => x.id === templateId);
    if (!t) return;
    setNewPlan({
      phases: t.phases.map(p => ({
        phase_name: p.phase_name,
        // Editing form uses plain strings so typing (incl. trailing spaces and
        // commas) doesn't get mangled by a live split/trim/filter round-trip.
        // Converted back to string[] at POST time (_phasesForApi below).
        exercises: Array.isArray(p.exercises) ? p.exercises.join(', ') : (p.exercises || ''),
        restrictions: Array.isArray(p.restrictions) ? p.restrictions.join(', ') : (p.restrictions || ''),
        weekly_goals: p.weekly_goals || '',
        duration_days: p.duration_days || null,
        description: p.description || '',
        completed: false,
      })),
      overall_notes: t.overall_notes || '',
      target_return_date: '',
    });
    toast.success(`Applied template: ${t.name}`);
  };

  // Convert the editing-time phase shape (exercises/restrictions as plain
  // strings) to the API shape (string[]). This is the ONLY place the
  // split/trim/filter happens — never on each keystroke — so the user can
  // type trailing spaces and commas freely in the form inputs.
  const _phasesForApi = (phases) => (phases || []).map(p => ({
    ...p,
    exercises: typeof p.exercises === 'string'
      ? p.exercises.split(',').map(s => s.trim()).filter(Boolean)
      : (p.exercises || []),
    restrictions: typeof p.restrictions === 'string'
      ? p.restrictions.split(',').map(s => s.trim()).filter(Boolean)
      : (p.restrictions || []),
  }));

  const handleSaveAsTemplate = async () => {
    const name = (templateNameInput || '').trim();
    if (!name) { toast.error('Give the template a name'); return; }
    setSavingTemplate(true);
    try {
      await axios.post(`${API}/injury-recovery/recovery-templates`, {
        name,
        description: null,
        phases: _phasesForApi(newPlan.phases),
        overall_notes: newPlan.overall_notes,
      });
      toast.success(`Template saved: ${name}`);
      setTemplateNameInput('');
      fetchTemplates();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleCreateNote = async (injuryId) => {
    if (!newNoteContent.trim()) { toast.error('Note content is required'); return; }
    try {
      await axios.post(`${API}/injury-recovery/injuries/${injuryId}/notes`, {
        injury_id: injuryId,
        content: newNoteContent.trim(),
        visible_to_staff_ids: [],
      });
      toast.success('Note added');
      setNewNoteContent('');
      fetchInjuryNotes(injuryId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add note');
    }
  };

  const handleDeleteNote = async (noteId, injuryId) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await axios.delete(`${API}/injury-recovery/injuries/notes/${noteId}`);
      toast.success('Note deleted');
      fetchInjuryNotes(injuryId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete note');
    }
  };

  const handleDeleteProgress = async (progressId) => {
    if (!window.confirm('Delete this progress entry? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/injury-recovery/progress/${progressId}`);
      toast.success('Progress entry deleted');
      if (selectedInjury?.id) await fetchInjuryDetails(selectedInjury.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete progress entry');
    }
  };

  // Fetch initial data
  useEffect(() => {
    fetchConfig();
    if (propAthleteId) {
      axios.get(`${API}/athletes/${propAthleteId}`).then(res => {
        setAthletes([res.data]);
        setSelectedAthlete(res.data.id);
        setLoading(false);
      }).catch(() => setLoading(false));
    } else {
      fetchAthletes();
      fetchActiveBoard();
      fetchTemplates();
    }
  }, [propAthleteId, fetchActiveBoard, fetchTemplates]);

  // Phase 74 — when the page is opened with `?athlete_id=<id>` (e.g. from the
  // Athlete 360 Health card "Open Injury & Recovery" button), pre-select that
  // athlete once the athletes list has loaded.
  useEffect(() => {
    if (!propAthleteId && queryAthleteId && athletes.length > 0) {
      const match = athletes.find(a => a.id === queryAthleteId);
      if (match && selectedAthlete !== queryAthleteId) {
        setSelectedAthlete(queryAthleteId);
      }
    }
  }, [queryAthleteId, athletes, propAthleteId, selectedAthlete]);

  // Fetch injuries when athlete changes
  useEffect(() => {
    if (selectedAthlete) {
      fetchAthleteInjuries(selectedAthlete);
    }
  }, [selectedAthlete]);

  // Fetch injury details when selected injury changes
  const fetchInjuryDetails = useCallback(async (injuryId) => {
    try {
      const response = await axios.get(`${API}/injury-recovery/injuries/${injuryId}`);
      setInjuryDetails(response.data);
      setAccess(response.data.access || { can_view: true, can_edit: false, can_edit_medical: false });
    } catch (error) {
      toast.error('Failed to load injury details');
    }
  }, []);

  useEffect(() => {
    if (selectedInjury?.id) {
      fetchInjuryDetails(selectedInjury.id);
      fetchInjuryNotes(selectedInjury.id);
    }
  }, [selectedInjury, fetchInjuryDetails, fetchInjuryNotes]);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/injury-recovery/config`);
      setConfig(response.data);
    } catch (error) {
      console.error('Failed to load config');
    }
  };

  const fetchAthletes = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/athletes`);
      const athleteList = response.data.athletes || response.data || [];
      setAthletes(Array.isArray(athleteList) ? athleteList : []);
    } catch (error) {
      toast.error('Failed to load athletes');
    } finally {
      setLoading(false);
    }
  };

  const fetchAthleteInjuries = async (athleteId) => {
    try {
      const response = await axios.get(`${API}/injury-recovery/injuries/athlete/${athleteId}`);
      // Sort newest first so the board/dropdown show the most relevant entries up top.
      const sortedInjuries = [...(response.data.injuries || [])].sort((a, b) => {
        const da = new Date(a.date_of_injury || a.created_at || 0).getTime();
        const db = new Date(b.date_of_injury || b.created_at || 0).getTime();
        return db - da;
      });
      setInjuries(sortedInjuries);
      setAccess(response.data.access || { can_view: true, can_edit: false, can_edit_medical: false });
      // Auto-select first active injury
      const activeInjury = sortedInjuries.find(i => i.status !== 'Recovered');
      if (activeInjury) {
        setSelectedInjury(activeInjury);
      } else if (sortedInjuries.length > 0) {
        setSelectedInjury(sortedInjuries[0]);
      } else {
        setSelectedInjury(null);
        setInjuryDetails(null);
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('You do not have permission to view this athlete\'s injuries');
      } else {
        toast.error('Failed to load injuries');
      }
      setInjuries([]);
    }
  };

  // Create injury
  const handleCreateInjury = async () => {
    const targetAthlete = selectedAthlete || quickInjuryAthlete;
    if (!targetAthlete) {
      toast.error('Select an athlete');
      return;
    }
    if (!newInjury.injury_type || !newInjury.body_area) {
      toast.error('Body area and injury type are required');
      return;
    }
    const payload = {
      athlete_id: targetAthlete,
      ...newInjury,
      date_of_injury: newInjury.date_of_injury || new Date().toISOString().slice(0, 10),
      expected_recovery_days: newInjury.expected_recovery_days
        ? parseInt(newInjury.expected_recovery_days) : null,
    };
    try {
      const response = await axios.post(`${API}/injury-recovery/injuries`, payload);
      toast.success('Injury record created');
      setCreateInjuryDialog(false);
      setNewInjury({
        injury_type: '', injury_category: 'Muscle', body_area: '', severity: 'Unknown',
        date_of_injury: '', expected_recovery_days: '', diagnosis_notes: '', cause: 'Unknown',
      });
      // If it was opened from Quick Report, route the staff member into the record.
      if (quickInjuryAthlete && !selectedAthlete) {
        setSelectedAthlete(quickInjuryAthlete);
        setQuickInjuryAthlete('');
      }
      if (targetAthlete === selectedAthlete) {
        fetchAthleteInjuries(selectedAthlete);
        setSelectedInjury(response.data.injury);
      }
      fetchActiveBoard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create injury');
    }
  };

  // Create recovery plan
  const handleCreatePlan = async () => {
    if (!selectedInjury) return;
    try {
      await axios.post(`${API}/injury-recovery/recovery-plans`, {
        injury_id: selectedInjury.id,
        athlete_id: selectedAthlete,
        phases: _phasesForApi(newPlan.phases),
        overall_notes: newPlan.overall_notes,
        target_return_date: newPlan.target_return_date || null
      });
      toast.success('Recovery plan created');
      setCreatePlanDialog(false);
      fetchInjuryDetails(selectedInjury.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create plan');
    }
  };

  // Add progress update
  const handleAddProgress = async () => {
    if (!selectedInjury) return;
    try {
      await axios.post(`${API}/injury-recovery/progress`, {
        injury_id: selectedInjury.id,
        athlete_id: selectedAthlete,
        ...newProgress
      });
      toast.success('Progress update recorded');
      setProgressDialog(false);
      setNewProgress({ recovery_percent: 0, pain_level: 5, mobility_rating: 5, fitness_readiness: 5, notes: '' });
      fetchInjuryDetails(selectedInjury.id);
      fetchAthleteInjuries(selectedAthlete);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record progress');
    }
  };

  // Create/Update clearance
  const handleClearance = async () => {
    if (!selectedInjury) return;
    try {
      // Check if clearance exists
      if (injuryDetails?.clearance) {
        await axios.put(`${API}/injury-recovery/clearance/${injuryDetails.clearance.id}`, newClearance);
      } else {
        await axios.post(`${API}/injury-recovery/clearance`, {
          injury_id: selectedInjury.id,
          athlete_id: selectedAthlete,
          ...newClearance
        });
      }
      toast.success(newClearance.clearance_status === 'Cleared' ? 'Athlete cleared to play!' : 'Clearance updated');
      setClearanceDialog(false);
      setNewClearance({ clearance_status: 'Not Cleared', notes: '', restrictions: '' });
      fetchInjuryDetails(selectedInjury.id);
      fetchAthleteInjuries(selectedAthlete);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update clearance');
    }
  };

  // Update injury status
  const handleUpdateStatus = async (newStatus) => {
    if (!selectedInjury) return;
    try {
      await axios.put(`${API}/injury-recovery/injuries/${selectedInjury.id}`, { status: newStatus });
      toast.success('Status updated');
      fetchAthleteInjuries(selectedAthlete);
      fetchInjuryDetails(selectedInjury.id);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // Complete phase
  const handleCompletePhase = async (phaseIndex) => {
    if (!injuryDetails?.recovery_plan) return;
    try {
      await axios.put(`${API}/injury-recovery/recovery-plans/${injuryDetails.recovery_plan.id}/phase/${phaseIndex}/complete`);
      toast.success('Phase completed');
      fetchInjuryDetails(selectedInjury.id);
    } catch (error) {
      toast.error('Failed to complete phase');
    }
  };

  // Helper functions
  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'Minor': return 'bg-yellow-100 text-yellow-800';
      case 'Moderate': return 'bg-orange-100 text-orange-800';
      case 'Severe': return 'bg-red-100 text-red-800';
      case 'Critical': return 'bg-red-600 text-white';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-500';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Injured': return 'bg-red-100 text-red-800';
      case 'In Recovery': return 'bg-blue-100 text-blue-800';
      case 'Recovered': return 'bg-emerald-100 text-emerald-800';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-500';
    }
  };

  const getClearanceColor = (status) => {
    return status === 'Cleared' ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800';
  };

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-8" data-testid="injury-recovery-page">
        <AthleteContextHeader athleteId={propAthleteId} moduleName="Injury & Recovery" />
        {/* Header */}
        <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <img src="/pfap-logo-icon.png" alt="PFAP" className="h-10 w-10 hidden sm:block" />
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Injury & Recovery
              </h2>
              <p className="text-gray-600 dark:text-gray-500 mt-1 text-sm">Track injuries, recovery plans, and return-to-play status</p>
            </div>
          </div>
          {isStaff && !isScopedMode && (
            <Button
              onClick={() => {
                setQuickInjuryAthlete('');
                setNewInjury({
                  injury_type: '', injury_category: 'Muscle', body_area: '',
                  severity: 'Unknown',
                  date_of_injury: new Date().toISOString().slice(0, 10),
                  expected_recovery_days: '', diagnosis_notes: '', cause: 'Unknown',
                });
                setCreateInjuryDialog(true);
              }}
              className="bg-red-600 hover:bg-red-700"
              data-testid="quick-report-injury-btn"
            >
              + Quick Report Injury
            </Button>
          )}
        </div>

        {/* Active Injuries Board — operational squad-wide view for staff */}
        {isStaff && !isScopedMode && (
          <Card className="mb-6" data-testid="active-injuries-board">
            <CardHeader className="cursor-pointer" onClick={() => setBoardExpanded(!boardExpanded)}>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <span>Active Injuries Board</span>
                    <Badge className="bg-red-100 text-red-800">{visibleBoardRows.length}</Badge>
                    {archivedCount > 0 && (
                      <Badge variant="outline" className="text-xs">
                        {archivedCount} archived
                      </Badge>
                    )}
                  </CardTitle>
                  <CardDescription>
                    Every athlete currently injured or in recovery — quick updates without drilling into each profile.
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  {archivedCount > 0 && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setShowArchived((v) => !v)}
                      data-testid="toggle-show-archived-injuries"
                    >
                      {showArchived ? 'Hide archived' : `Show archived (${archivedCount})`}
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => setBoardExpanded(!boardExpanded)}>
                    {boardExpanded ? 'Collapse' : 'Expand'}
                  </Button>
                </div>
              </div>
            </CardHeader>
            {boardExpanded && (
              <CardContent>
                {boardLoading ? (
                  <p className="py-4 text-center text-gray-500 dark:text-gray-500 text-sm">Loading…</p>
                ) : visibleBoardRows.length === 0 ? (
                  <p className="py-4 text-center text-emerald-600 text-sm">
                    {archivedCount > 0 && !showArchived
                      ? `All active injuries archived from view. ${archivedCount} hidden — click "Show archived" to restore.`
                      : 'All athletes currently healthy ✓'}
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left border-b border-gray-200 dark:border-gray-700">
                          <th className="py-2 pr-3">Athlete</th>
                          <th className="py-2 pr-3">Injury</th>
                          <th className="py-2 pr-3">Severity</th>
                          <th className="py-2 pr-3">Recovery</th>
                          <th className="py-2 pr-3">Phase</th>
                          <th className="py-2 pr-3">Latest note</th>
                          <th className="py-2 pr-3">Cleared?</th>
                          <th className="py-2 pr-3 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleBoardRows.map((row) => {
                          const pct = row.latest_progress?.recovery_percent ?? 0;
                          const cleared = row.clearance?.clearance_status === 'Cleared';
                          const isArchived = archivedIds.has(row.injury_id);
                          return (
                            <tr
                              key={row.injury_id}
                              className={`border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-900 ${isArchived ? 'opacity-60' : ''}`}
                              data-testid={`board-row-${row.injury_id}`}
                            >
                              <td className="py-2 pr-3">
                                <div className="font-medium text-gray-900 dark:text-gray-100">{row.athlete?.full_name || 'Unknown'}</div>
                                <div className="text-xs text-gray-500 dark:text-gray-500">{row.athlete?.squad} · {row.athlete?.position}</div>
                              </td>
                              <td className="py-2 pr-3">
                                <div>{row.injury_type}</div>
                                <div className="text-xs text-gray-500 dark:text-gray-500">{row.body_area}</div>
                              </td>
                              <td className="py-2 pr-3">
                                <Badge className={getSeverityColor(row.severity)}>{row.severity || '—'}</Badge>
                              </td>
                              <td className="py-2 pr-3 min-w-[120px]">
                                <div className="flex items-center gap-2">
                                  <Progress value={pct} className="h-2 w-20" />
                                  <span className="text-xs text-gray-600 dark:text-gray-400">{pct}%</span>
                                </div>
                              </td>
                              <td className="py-2 pr-3 text-xs text-gray-600 dark:text-gray-400">
                                {row.current_phase_name || '—'}
                              </td>
                              <td className="py-2 pr-3 text-xs text-gray-600 dark:text-gray-400 max-w-[220px] truncate">
                                {row.latest_note?.snippet
                                  ? (<span title={row.latest_note.snippet}><StickyNote className="inline h-3 w-3 mr-1" />{row.latest_note.snippet}</span>)
                                  : '—'}
                              </td>
                              <td className="py-2 pr-3">
                                {cleared ? (
                                  <Badge className="bg-emerald-100 text-emerald-800">Cleared</Badge>
                                ) : (
                                  <Badge className="bg-red-100 text-red-800">Not cleared</Badge>
                                )}
                              </td>
                              <td className="py-2 pr-3 text-right">
                                <div className="flex gap-1 justify-end flex-wrap">
                                  <Button
                                    size="sm" variant="outline"
                                    onClick={() => {
                                      setQuickProgressPct(pct);
                                      setQuickProgressNote('');
                                      setQuickProgressDialog({
                                        injury_id: row.injury_id,
                                        athlete_id: row.athlete?.id,
                                        athlete_name: row.athlete?.full_name,
                                      });
                                    }}
                                    data-testid={`board-quick-update-${row.injury_id}`}
                                  >
                                    Update
                                  </Button>
                                  <Button
                                    size="sm"
                                    className={cleared ? 'bg-gray-200 text-gray-800' : 'bg-emerald-600 hover:bg-emerald-700 text-white'}
                                    onClick={() => {
                                      setQuickClearStatus(cleared ? 'Not Cleared' : 'Cleared');
                                      setQuickClearRestrictions(row.clearance?.restrictions || '');
                                      setQuickClearDialog({
                                        injury_id: row.injury_id,
                                        athlete_id: row.athlete?.id,
                                        athlete_name: row.athlete?.full_name,
                                      });
                                    }}
                                    data-testid={`board-clear-${row.injury_id}`}
                                  >
                                    {cleared ? 'Unclear' : 'Clear'}
                                  </Button>
                                  <Button
                                    size="sm" variant="ghost"
                                    onClick={() => {
                                      setSelectedAthlete(row.athlete?.id);
                                    }}
                                    data-testid={`board-open-${row.injury_id}`}
                                  >
                                    Open plan
                                  </Button>
                                  {isArchived ? (
                                    <Button
                                      size="sm" variant="ghost"
                                      title="Restore to Active View"
                                      onClick={() => handleUnarchiveRow(row.injury_id)}
                                      data-testid={`board-unarchive-${row.injury_id}`}
                                    >
                                      <ArchiveRestore className="h-3.5 w-3.5" />
                                    </Button>
                                  ) : (
                                    <Button
                                      size="sm" variant="ghost"
                                      title="Archive from Active View (record is not deleted)"
                                      onClick={() => handleArchiveRow(row.injury_id)}
                                      data-testid={`board-archive-${row.injury_id}`}
                                    >
                                      <Archive className="h-3.5 w-3.5" />
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            )}
          </Card>
        )}

        {/* Athlete & Injury Selector */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-[200px] max-w-[300px]">
                <Label>Athlete</Label>
                <SearchableAthleteSelect
                  athletes={athletes}
                  value={selectedAthlete}
                  onChange={setSelectedAthlete}
                  placeholder="Choose an athlete..."
                  disabled={isScopedMode}
                  data-testid="athlete-select"
                />
              </div>
              
              {selectedAthlete && injuries.length > 0 && (
                <div className="flex-1 min-w-[200px] max-w-[350px]">
                  <Label>Injury Record</Label>
                  <Select 
                    value={selectedInjury?.id || ''} 
                    onValueChange={(id) => setSelectedInjury(injuries.find(i => i.id === id))}
                  >
                    <SelectTrigger data-testid="injury-select">
                      <SelectValue placeholder="Select injury..." />
                    </SelectTrigger>
                    <SelectContent>
                      {(shouldLimitForContractFixture(selectedAthlete) ? injuries.slice(0, 3) : injuries).map(injury => (
                        <SelectItem key={injury.id} value={injury.id}>
                          {injury.injury_type} ({injury.body_area}) - {injury.status}
                        </SelectItem>
                      ))}
                      {shouldLimitForContractFixture(selectedAthlete) && injuries.length > 3 && (
                        <div className="px-2 py-1 text-xs text-muted-foreground border-t mt-1">
                          Showing 3 most recent · {injuries.length - 3} older hidden
                        </div>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {access.can_edit_medical && selectedAthlete && (
                <Button 
                  onClick={() => setCreateInjuryDialog(true)}
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="create-injury-btn"
                >
                  + Report Injury
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Main Content */}
        {!selectedAthlete ? (
          <Card>
            <CardContent className="py-12 text-center text-gray-500 dark:text-gray-500">
              Select an athlete to view their injury records
            </CardContent>
          </Card>
        ) : injuries.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <div className="text-emerald-600 text-4xl mb-4">✓</div>
              <p className="text-gray-600 dark:text-gray-500">No injury records for this athlete</p>
              {access.can_edit_medical && (
                <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">Click "Report Injury" to create a new record</p>
              )}
            </CardContent>
          </Card>
        ) : !selectedInjury ? (
          <Card>
            <CardContent className="py-12 text-center text-gray-500 dark:text-gray-500">
              Select an injury record to view details
            </CardContent>
          </Card>
        ) : injuryDetails ? (
          <>
            {/* Injury Overview Card */}
            <Card className="mb-6">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="flex items-center gap-3">
                      <span>{selectedInjury.injury_type}</span>
                      <Badge className={getStatusColor(selectedInjury.status)}>
                        {selectedInjury.status}
                      </Badge>
                      <Badge className={getSeverityColor(selectedInjury.severity)}>
                        {selectedInjury.severity}
                      </Badge>
                      {injuryDetails.clearance && (
                        <Badge className={getClearanceColor(injuryDetails.clearance.clearance_status)}>
                          {injuryDetails.clearance.clearance_status}
                        </Badge>
                      )}
                    </CardTitle>
                    <CardDescription>
                      {selectedInjury.body_area} • Injured: {new Date(selectedInjury.date_of_injury).toLocaleDateString()}
                      {selectedInjury.cause && selectedInjury.cause !== 'Unknown' && ` • Cause: ${selectedInjury.cause}`}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    {access.can_edit && selectedInjury.status !== 'Recovered' && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => setProgressDialog(true)}>
                          + Update Progress
                        </Button>
                        {access.can_edit_medical && (
                          <Button 
                            size="sm" 
                            className="bg-pfap-green hover:bg-pfap-green/90"
                            onClick={() => {
                              setNewClearance({
                                clearance_status: injuryDetails.clearance?.clearance_status || 'Not Cleared',
                                notes: injuryDetails.clearance?.notes || '',
                                restrictions: injuryDetails.clearance?.restrictions || ''
                              });
                              setClearanceDialog(true);
                            }}
                          >
                            {injuryDetails.clearance ? 'Update Clearance' : 'Add Clearance'}
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Recovery Progress */}
                  <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-500">Recovery</span>
                      <span className="text-2xl font-bold text-emerald-600">
                        {injuryDetails.progress_summary?.latest_recovery_percent || 0}%
                      </span>
                    </div>
                    <Progress value={injuryDetails.progress_summary?.latest_recovery_percent || 0} className="h-2" />
                    {injuryDetails.progress_summary?.trend && (
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                        Trend: {injuryDetails.progress_summary.trend}
                      </p>
                    )}
                  </div>
                  
                  {/* Pain Level */}
                  <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-500">Pain Level</span>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-2xl font-bold text-red-600">
                        {injuryDetails.progress_summary?.latest_pain_level || '-'}
                      </span>
                      <span className="text-sm text-gray-500 dark:text-gray-500">/10</span>
                    </div>
                  </div>
                  
                  {/* Mobility */}
                  <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-500">Mobility</span>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-2xl font-bold text-blue-600">
                        {injuryDetails.progress_summary?.latest_mobility || '-'}
                      </span>
                      <span className="text-sm text-gray-500 dark:text-gray-500">/10</span>
                    </div>
                  </div>
                  
                  {/* Fitness */}
                  <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-500">Fitness Ready</span>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-2xl font-bold text-purple-600">
                        {injuryDetails.progress_summary?.latest_fitness || '-'}
                      </span>
                      <span className="text-sm text-gray-500 dark:text-gray-500">/10</span>
                    </div>
                  </div>
                </div>
                
                {/* Medical Notes */}
                {selectedInjury.diagnosis_notes && (
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                    <p className="text-xs font-medium text-blue-600 mb-1">Diagnosis Notes</p>
                    <p className="text-sm text-gray-700 dark:text-gray-300">{selectedInjury.diagnosis_notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="mb-4">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="recovery-plan">Recovery Plan</TabsTrigger>
                <TabsTrigger value="progress">Progress Log</TabsTrigger>
                <TabsTrigger value="notes" data-testid="tab-notes"><StickyNote className="w-3 h-3 mr-1 inline" />Internal Notes{injuryNotes.length > 0 && <span className="ml-1 text-xs">({injuryNotes.length})</span>}</TabsTrigger>
                <TabsTrigger value="history">History</TabsTrigger>
              </TabsList>

              {/* Overview Tab */}
              <TabsContent value="overview">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Injury Details */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Injury Details</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <dl className="space-y-3">
                        <div className="flex justify-between">
                          <dt className="text-gray-500 dark:text-gray-500">Type:</dt>
                          <dd className="font-medium">{selectedInjury.injury_type}</dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-gray-500 dark:text-gray-500">Body Area:</dt>
                          <dd className="font-medium">{selectedInjury.body_area}</dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-gray-500 dark:text-gray-500">Severity:</dt>
                          <dd><Badge className={getSeverityColor(selectedInjury.severity)}>{selectedInjury.severity}</Badge></dd>
                        </div>
                        {selectedInjury.injury_category && (
                          <div className="flex justify-between">
                            <dt className="text-gray-500 dark:text-gray-500">Classification:</dt>
                            <dd><Badge variant="outline">{selectedInjury.injury_category}</Badge></dd>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <dt className="text-gray-500 dark:text-gray-500">Cause:</dt>
                          <dd className="font-medium">{selectedInjury.cause || 'Unknown'}</dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-gray-500 dark:text-gray-500">Date of Injury:</dt>
                          <dd className="font-medium">{new Date(selectedInjury.date_of_injury).toLocaleDateString()}</dd>
                        </div>
                        {selectedInjury.expected_recovery_days && (
                          <div className="flex justify-between">
                            <dt className="text-gray-500 dark:text-gray-500">Expected Recovery:</dt>
                            <dd className="font-medium">{selectedInjury.expected_recovery_days} days</dd>
                          </div>
                        )}
                        {selectedInjury.actual_recovery_days && (
                          <div className="flex justify-between">
                            <dt className="text-gray-500 dark:text-gray-500">Actual Recovery:</dt>
                            <dd className="font-medium">{selectedInjury.actual_recovery_days} days</dd>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <dt className="text-gray-500 dark:text-gray-500">Medical Staff:</dt>
                          <dd className="font-medium">{selectedInjury.medical_staff_name || 'N/A'}</dd>
                        </div>
                      </dl>
                      
                      {access.can_edit && selectedInjury.status !== 'Recovered' && (
                        <div className="mt-4 pt-4 border-t">
                          <Label>Update Status</Label>
                          <Select value={selectedInjury.status} onValueChange={handleUpdateStatus}>
                            <SelectTrigger className="mt-2">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Injured">Injured</SelectItem>
                              <SelectItem value="In Recovery">In Recovery</SelectItem>
                              <SelectItem value="Recovered">Recovered</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Clearance Status */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Return-to-Play Clearance</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {injuryDetails.clearance ? (
                        <div className="space-y-4">
                          <div className="flex items-center gap-3">
                            <Badge className={`text-lg px-4 py-2 ${getClearanceColor(injuryDetails.clearance.clearance_status)}`}>
                              {injuryDetails.clearance.clearance_status}
                            </Badge>
                          </div>
                          {injuryDetails.clearance.clearance_date && (
                            <p className="text-sm text-gray-600 dark:text-gray-500">
                              Cleared on: {new Date(injuryDetails.clearance.clearance_date).toLocaleDateString()}
                            </p>
                          )}
                          <p className="text-sm text-gray-600 dark:text-gray-500">
                            Approved by: {injuryDetails.clearance.approved_by_name}
                          </p>
                          {injuryDetails.clearance.restrictions && (
                            <div className="p-3 bg-amber-50 rounded-lg">
                              <p className="text-xs font-medium text-amber-600 mb-1">Restrictions</p>
                              <p className="text-sm text-gray-700 dark:text-gray-300">{injuryDetails.clearance.restrictions}</p>
                            </div>
                          )}
                          {injuryDetails.clearance.notes && (
                            <div className="p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                              <p className="text-xs font-medium text-gray-600 dark:text-gray-500 mb-1">Notes</p>
                              <p className="text-sm text-gray-700 dark:text-gray-300">{injuryDetails.clearance.notes}</p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-center py-6">
                          <div className="text-amber-500 text-4xl mb-2">⚠️</div>
                          <p className="text-gray-600 dark:text-gray-500">No clearance record</p>
                          <p className="text-sm text-gray-500 dark:text-gray-500">Athlete is not cleared to play</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* Recovery Plan Tab */}
              <TabsContent value="recovery-plan">
                <Card>
                  <CardHeader>
                    <div className="flex justify-between items-center">
                      <CardTitle>Recovery Plan</CardTitle>
                      {access.can_edit_medical && !injuryDetails.recovery_plan && selectedInjury.status !== 'Recovered' && (
                        <Button onClick={() => setCreatePlanDialog(true)} className="bg-blue-600 hover:bg-blue-700">
                          + Create Plan
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {injuryDetails.recovery_plan ? (
                      <div className="space-y-6">
                        {injuryDetails.recovery_plan.target_return_date && (
                          <div className="p-3 bg-emerald-50 rounded-lg">
                            <p className="text-sm">
                              <span className="font-medium text-emerald-700">Target Return:</span>{' '}
                              {new Date(injuryDetails.recovery_plan.target_return_date).toLocaleDateString()}
                            </p>
                          </div>
                        )}
                        
                        {/* Phases */}
                        <div className="space-y-4">
                          {(injuryDetails.recovery_plan.phases || []).map((phase, index) => (
                            <div 
                              key={index}
                              className={`p-4 rounded-lg border-2 ${
                                phase.completed 
                                  ? 'bg-emerald-50 border-emerald-200' 
                                  : index + 1 === injuryDetails.recovery_plan.current_phase
                                    ? 'bg-blue-50 border-blue-300'
                                    : 'bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700'
                              }`}
                            >
                              <div className="flex justify-between items-start mb-2">
                                <div className="flex items-center gap-2">
                                  {phase.completed ? (
                                    <span className="text-emerald-600 text-lg">✓</span>
                                  ) : index + 1 === injuryDetails.recovery_plan.current_phase ? (
                                    <span className="text-blue-600 text-lg">●</span>
                                  ) : (
                                    <span className="text-gray-400 dark:text-gray-500 text-lg">○</span>
                                  )}
                                  <span className="font-semibold">{phase.phase_name}</span>
                                  {phase.completed && (
                                    <Badge className="bg-emerald-100 text-emerald-800">Completed</Badge>
                                  )}
                                  {index + 1 === injuryDetails.recovery_plan.current_phase && !phase.completed && (
                                    <Badge className="bg-blue-100 text-blue-800">Current</Badge>
                                  )}
                                </div>
                                {access.can_edit && !phase.completed && index + 1 === injuryDetails.recovery_plan.current_phase && (
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => handleCompletePhase(index)}
                                  >
                                    Mark Complete
                                  </Button>
                                )}
                              </div>
                              
                              {phase.description && (
                                <p className="text-sm text-gray-600 dark:text-gray-500 mb-2">{phase.description}</p>
                              )}
                              
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                                {phase.exercises?.length > 0 && (
                                  <div>
                                    <p className="text-xs font-medium text-gray-500 dark:text-gray-500 mb-1">Exercises:</p>
                                    <ul className="text-sm text-gray-700 dark:text-gray-300 list-disc list-inside">
                                      {phase.exercises.map((ex, i) => <li key={i}>{ex}</li>)}
                                    </ul>
                                  </div>
                                )}
                                {phase.restrictions?.length > 0 && (
                                  <div>
                                    <p className="text-xs font-medium text-red-500 mb-1">Restrictions:</p>
                                    <ul className="text-sm text-gray-700 dark:text-gray-300 list-disc list-inside">
                                      {phase.restrictions.map((r, i) => <li key={i}>{r}</li>)}
                                    </ul>
                                  </div>
                                )}
                              </div>
                              
                              {phase.weekly_goals && (
                                <div className="mt-2 p-2 bg-white dark:bg-gray-800 rounded">
                                  <p className="text-xs font-medium text-gray-500 dark:text-gray-500">Weekly Goals:</p>
                                  <p className="text-sm text-gray-700 dark:text-gray-300">{phase.weekly_goals}</p>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                        
                        {injuryDetails.recovery_plan.overall_notes && (
                          <div className="p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                            <p className="text-xs font-medium text-gray-500 dark:text-gray-500 mb-1">Overall Notes:</p>
                            <p className="text-sm text-gray-700 dark:text-gray-300">{injuryDetails.recovery_plan.overall_notes}</p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500 dark:text-gray-500">
                        No recovery plan created yet
                        {access.can_edit_medical && ' - Click "Create Plan" to add one'}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Progress Log Tab */}
              <TabsContent value="progress">
                <Card>
                  <CardHeader>
                    <div className="flex justify-between items-center">
                      <div>
                        <CardTitle>Progress Log</CardTitle>
                        <CardDescription>{injuryDetails.progress_logs?.length || 0} updates recorded</CardDescription>
                      </div>
                      {access.can_edit && selectedInjury.status !== 'Recovered' && (
                        <Button onClick={() => setProgressDialog(true)} variant="outline">
                          + Add Update
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {(injuryDetails.progress_logs || []).length === 0 ? (
                      <p className="text-center py-8 text-gray-500 dark:text-gray-500">No progress updates yet</p>
                    ) : (
                      <div className="space-y-4">
                        {injuryDetails.progress_logs.map((log, index) => (
                          <div 
                            key={log.id}
                            className={`p-4 rounded-lg ${index === 0 ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50 dark:bg-gray-900'}`}
                          >
                            <div className="flex justify-between items-start mb-3">
                              <div>
                                <span className="font-medium">{log.recorded_by_name}</span>
                                {index === 0 && <Badge className="ml-2 bg-blue-100 text-blue-800">Latest</Badge>}
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-500 dark:text-gray-500">
                                  {new Date(log.recorded_at).toLocaleString()}
                                </span>
                                {(user?.role === 'Admin' || log.recorded_by === user?.id) && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-6 w-6 p-0 text-red-500"
                                    onClick={() => handleDeleteProgress(log.id)}
                                    data-testid={`delete-progress-${log.id}`}
                                    title="Delete this progress entry"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                )}
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                              <div>
                                <p className="text-xs text-gray-500 dark:text-gray-500">Recovery</p>
                                <p className="text-lg font-bold text-emerald-600">{log.recovery_percent}%</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 dark:text-gray-500">Pain Level</p>
                                <p className="text-lg font-bold text-red-600">{log.pain_level}/10</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 dark:text-gray-500">Mobility</p>
                                <p className="text-lg font-bold text-blue-600">{log.mobility_rating}/10</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 dark:text-gray-500">Fitness</p>
                                <p className="text-lg font-bold text-purple-600">{log.fitness_readiness}/10</p>
                              </div>
                            </div>
                            
                            {log.notes && (
                              <p className="text-sm text-gray-600 dark:text-gray-500 italic">"{log.notes}"</p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* History Tab */}
              <TabsContent value="history">
                <InjuryHistory athleteId={selectedAthlete} currentInjuryId={selectedInjury?.id} />
              </TabsContent>

              {/* Internal Notes Tab — visible to Admin & Staff with per-note targeting */}
              <TabsContent value="notes">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2"><StickyNote className="w-4 h-4" /> Internal Notes</CardTitle>
                    <CardDescription>Private observations between staff (physio, coaches, admin). Hidden from players and parents.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {isStaff && (
                      <div className="space-y-2">
                        <Textarea
                          value={newNoteContent}
                          onChange={(e) => setNewNoteContent(e.target.value)}
                          placeholder="Add an observation, insight, or update..."
                          rows={3}
                          data-testid="new-note-textarea"
                        />
                        <div className="flex justify-end">
                          <Button size="sm" onClick={() => handleCreateNote(selectedInjury.id)} data-testid="add-note-btn">Add Note</Button>
                        </div>
                      </div>
                    )}
                    {injuryNotes.length === 0 ? (
                      <p className="text-center py-6 text-gray-500 italic">No notes for this injury yet.</p>
                    ) : (
                      <div className="space-y-3">
                        {injuryNotes.map(n => (
                          <div key={n.id} className="p-3 rounded-lg bg-amber-50/60 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-900/30" data-testid={`note-${n.id}`}>
                            <div className="flex justify-between items-start mb-1">
                              <div>
                                <span className="font-medium text-sm">{n.author_name || 'Unknown'}</span>
                                <span className="text-xs text-gray-500 ml-2">{n.author_role}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-500">{new Date(n.created_at).toLocaleString()}</span>
                                {(user?.role === 'Admin' || n.author_id === user?.id) && (
                                  <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-500" onClick={() => handleDeleteNote(n.id, selectedInjury.id)} data-testid={`delete-note-${n.id}`}>
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                )}
                              </div>
                            </div>
                            <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{n.content}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </>
        ) : (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
          </div>
        )}

        {/* Create Injury Dialog */}
        <Dialog open={createInjuryDialog} onOpenChange={setCreateInjuryDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Report Injury</DialogTitle>
              <DialogDescription>Fast entry — only 3 fields required. Advanced details are optional.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
              {/* Athlete picker (only shown when opened via Quick Report) */}
              {!selectedAthlete && (
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Athlete *</Label>
                  <SearchableAthleteSelect
                    athletes={athletes}
                    value={quickInjuryAthlete}
                    onChange={setQuickInjuryAthlete}
                    placeholder="Pick athlete..."
                    data-testid="quick-injury-athlete-select"
                  />
                </div>
              )}

              {/* STEP 1 — Body Area */}
              <div>
                <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Body Area *</Label>
                <Select
                  value={newInjury.body_area}
                  onValueChange={(v) => setNewInjury({ ...newInjury, body_area: v, injury_type: '' })}
                >
                  <SelectTrigger data-testid="injury-body-area-select">
                    <SelectValue placeholder="Select a body area..." />
                  </SelectTrigger>
                  <SelectContent>
                    {config?.body_areas?.map(area => (
                      <SelectItem key={area} value={area}>{area}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* STEP 2 — Injury Type (filtered by body area) */}
              <div>
                <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Injury Type *</Label>
                <Select
                  value={newInjury.injury_type}
                  onValueChange={(v) => setNewInjury({ ...newInjury, injury_type: v })}
                  disabled={!newInjury.body_area}
                >
                  <SelectTrigger data-testid="injury-type-select">
                    <SelectValue placeholder={newInjury.body_area ? 'Select an injury type...' : 'Pick a body area first'} />
                  </SelectTrigger>
                  <SelectContent>
                    {(() => {
                      const mapping = config?.body_part_injuries || {};
                      const suggested = mapping[newInjury.body_area] || [];
                      const full = config?.injury_types || [];
                      const hasSuggestions = suggested.length > 0;
                      const rest = full.filter(t => !suggested.includes(t));
                      return (
                        <>
                          {hasSuggestions && (
                            <>
                              <div className="px-2 py-1 text-[10px] uppercase text-gray-500 tracking-wide">Suggested for {newInjury.body_area}</div>
                              {suggested.map(t => (<SelectItem key={`s-${t}`} value={t}>{t}</SelectItem>))}
                              {rest.length > 0 && <div className="px-2 py-1 text-[10px] uppercase text-gray-500 tracking-wide border-t border-gray-100 dark:border-gray-700 mt-1">All injury types</div>}
                            </>
                          )}
                          {rest.map(t => (<SelectItem key={`a-${t}`} value={t}>{t}</SelectItem>))}
                        </>
                      );
                    })()}
                  </SelectContent>
                </Select>
              </div>

              {/* STEP 3 — Severity (includes Unknown default) */}
              <div>
                <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Severity</Label>
                <Select value={newInjury.severity} onValueChange={(v) => setNewInjury({ ...newInjury, severity: v })}>
                  <SelectTrigger data-testid="injury-severity-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Unknown">Unknown (needs assessment)</SelectItem>
                    <SelectItem value="Minor">Minor</SelectItem>
                    <SelectItem value="Moderate">Moderate</SelectItem>
                    <SelectItem value="Severe">Severe</SelectItem>
                    <SelectItem value="Critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* ADVANCED (collapsed by default) */}
              <details className="group rounded-lg border border-gray-200 dark:border-gray-700 p-3" data-testid="injury-advanced-details">
                <summary className="cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300 select-none">
                  Advanced details (optional)
                </summary>
                <div className="mt-4 space-y-3">
                  <div>
                    <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Classification</Label>
                    <Select value={newInjury.injury_category || 'Muscle'} onValueChange={(v) => setNewInjury({ ...newInjury, injury_category: v })}>
                      <SelectTrigger data-testid="injury-category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Muscle">Muscle</SelectItem>
                        <SelectItem value="Ligament">Ligament</SelectItem>
                        <SelectItem value="Tendon">Tendon</SelectItem>
                        <SelectItem value="Bone">Bone</SelectItem>
                        <SelectItem value="Growth-related">Growth-related</SelectItem>
                        <SelectItem value="Joint">Joint</SelectItem>
                        <SelectItem value="Fatigue">Fatigue</SelectItem>
                        <SelectItem value="Concussion">Concussion</SelectItem>
                        <SelectItem value="Other">Other (specify)</SelectItem>
                      </SelectContent>
                    </Select>
                    {newInjury.injury_category === 'Other' && (
                      <Input
                        className="mt-2"
                        placeholder="Describe the classification..."
                        value={newInjury.injury_category_other || ''}
                        onChange={(e) => setNewInjury({ ...newInjury, injury_category_other: e.target.value })}
                        data-testid="injury-category-other-input"
                      />
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Date of Injury</Label>
                      <Input
                        type="date"
                        value={newInjury.date_of_injury || new Date().toISOString().slice(0, 10)}
                        onChange={(e) => setNewInjury({...newInjury, date_of_injury: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Cause</Label>
                      <Select value={newInjury.cause} onValueChange={(v) => setNewInjury({...newInjury, cause: v})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Match">Match</SelectItem>
                          <SelectItem value="Training">Training</SelectItem>
                          <SelectItem value="Pre-existing">Pre-existing</SelectItem>
                          <SelectItem value="Unknown">Unknown</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label>Expected Recovery (days)</Label>
                    <Input
                      type="number"
                      value={newInjury.expected_recovery_days}
                      onChange={(e) => setNewInjury({...newInjury, expected_recovery_days: e.target.value})}
                      placeholder="e.g., 21"
                    />
                  </div>
                  <div>
                    <Label>Diagnosis Notes</Label>
                    <Textarea
                      value={newInjury.diagnosis_notes}
                      onChange={(e) => setNewInjury({...newInjury, diagnosis_notes: e.target.value})}
                      placeholder="Clinical findings, imaging results, etc."
                    />
                  </div>
                </div>
              </details>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateInjuryDialog(false)}>Cancel</Button>
              <Button onClick={handleCreateInjury} className="bg-red-600 hover:bg-red-700" data-testid="submit-injury-btn">
                Report Injury
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Recovery Plan Dialog */}
        <Dialog open={createPlanDialog} onOpenChange={setCreatePlanDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create Recovery Plan</DialogTitle>
              <DialogDescription>Apply a saved template to fast-forward, or build from scratch</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
              {/* Apply Template shortcut */}
              {templates.length > 0 && (
                <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-100 dark:border-blue-900">
                  <Label className="text-xs font-semibold text-blue-700 dark:text-blue-400 uppercase tracking-wide">Apply a template</Label>
                  <Select onValueChange={handleApplyTemplate}>
                    <SelectTrigger className="mt-1" data-testid="apply-template-select">
                      <SelectValue placeholder="Pick a saved plan template..." />
                    </SelectTrigger>
                    <SelectContent>
                      {templates.map(t => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.name} · {t.phases?.length || 0} phases
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <Label>Target Return Date</Label>
                <Input
                  type="date"
                  value={newPlan.target_return_date}
                  onChange={(e) => setNewPlan({...newPlan, target_return_date: e.target.value})}
                />
              </div>

              {/* Phases */}
              <div className="space-y-3">
                {newPlan.phases.map((phase, index) => (
                  <div key={index} className="p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                    <p className="font-medium mb-2">{phase.phase_name}</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Exercises (comma separated)</Label>
                        <Input
                          placeholder="e.g., Pool walking, Stretching"
                          value={phase.exercises}
                          onChange={(e) => {
                            const phases = [...newPlan.phases];
                            phases[index] = { ...phases[index], exercises: e.target.value };
                            setNewPlan({...newPlan, phases});
                          }}
                          data-testid={`phase-${index}-exercises`}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Restrictions (comma separated)</Label>
                        <Input
                          placeholder="e.g., No running, No contact"
                          value={phase.restrictions}
                          onChange={(e) => {
                            const phases = [...newPlan.phases];
                            phases[index] = { ...phases[index], restrictions: e.target.value };
                            setNewPlan({...newPlan, phases});
                          }}
                          data-testid={`phase-${index}-restrictions`}
                        />
                      </div>
                    </div>
                    <div className="mt-2">
                      <Label className="text-xs">Weekly Goals</Label>
                      <Input
                        placeholder="Goals for this phase"
                        value={phase.weekly_goals}
                        onChange={(e) => {
                          const phases = [...newPlan.phases];
                          phases[index].weekly_goals = e.target.value;
                          setNewPlan({...newPlan, phases});
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div>
                <Label>Overall Notes</Label>
                <Textarea
                  value={newPlan.overall_notes}
                  onChange={(e) => setNewPlan({...newPlan, overall_notes: e.target.value})}
                  placeholder="General recovery guidelines..."
                />
              </div>

              {/* Save as Template */}
              <div className="p-3 rounded-lg border border-dashed border-gray-300 dark:border-gray-700">
                <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Save as reusable template</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    placeholder="e.g., Hamstring strain — 6 weeks"
                    value={templateNameInput}
                    onChange={(e) => setTemplateNameInput(e.target.value)}
                    data-testid="template-name-input"
                  />
                  <Button
                    variant="outline"
                    onClick={handleSaveAsTemplate}
                    disabled={savingTemplate || !templateNameInput.trim()}
                    data-testid="save-template-btn"
                  >
                    {savingTemplate ? 'Saving…' : 'Save as template'}
                  </Button>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreatePlanDialog(false)}>Cancel</Button>
              <Button onClick={handleCreatePlan} className="bg-blue-600 hover:bg-blue-700">
                Create Plan
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Quick Clearance mini-dialog (from Active Injuries Board) */}
        <Dialog open={!!quickClearDialog} onOpenChange={(v) => !v && setQuickClearDialog(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {quickClearStatus === 'Cleared' ? 'Clear to Play' : 'Update Clearance'}
              </DialogTitle>
              <DialogDescription>
                {quickClearDialog?.athlete_name} — one click return-to-play update.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-4">
              <div>
                <Label>Clearance Status</Label>
                <Select value={quickClearStatus} onValueChange={setQuickClearStatus}>
                  <SelectTrigger data-testid="quick-clear-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Cleared">Cleared to play</SelectItem>
                    <SelectItem value="Not Cleared">Not cleared</SelectItem>
                    <SelectItem value="Cleared with Restrictions">Cleared with restrictions</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {(quickClearStatus === 'Cleared with Restrictions' || quickClearStatus === 'Not Cleared') && (
                <div>
                  <Label>Restrictions (optional)</Label>
                  <Input
                    value={quickClearRestrictions}
                    onChange={(e) => setQuickClearRestrictions(e.target.value)}
                    placeholder="e.g., No contact for 1 week"
                    data-testid="quick-clear-restrictions-input"
                  />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setQuickClearDialog(null)}>Cancel</Button>
              <Button onClick={handleQuickClear} className="bg-emerald-600 hover:bg-emerald-700" data-testid="quick-clear-submit-btn">
                Confirm
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Quick Progress mini-dialog (from Active Injuries Board) */}
        <Dialog open={!!quickProgressDialog} onOpenChange={(v) => !v && setQuickProgressDialog(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Quick progress update</DialogTitle>
              <DialogDescription>
                {quickProgressDialog?.athlete_name} — recovery % + optional note.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <div className="flex justify-between mb-2">
                  <Label>Recovery %</Label>
                  <span className="text-emerald-600 font-bold">{quickProgressPct}%</span>
                </div>
                <Slider value={[quickProgressPct]} onValueChange={([v]) => setQuickProgressPct(v)} max={100} step={5} data-testid="quick-progress-slider" />
              </div>
              <div>
                <Label>Note (optional)</Label>
                <Textarea
                  value={quickProgressNote}
                  onChange={(e) => setQuickProgressNote(e.target.value)}
                  placeholder="Improvement this week / flag for coach"
                  data-testid="quick-progress-note-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setQuickProgressDialog(null)}>Cancel</Button>
              <Button onClick={handleQuickProgress} className="bg-pfap-green hover:bg-pfap-green/90" data-testid="quick-progress-submit-btn">
                Record
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Progress Update Dialog */}
        <Dialog open={progressDialog} onOpenChange={setProgressDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Update Recovery Progress</DialogTitle>
              <DialogDescription>Record current recovery metrics</DialogDescription>
            </DialogHeader>
            <div className="space-y-6 py-4">
              <div>
                <div className="flex justify-between mb-2">
                  <Label>Recovery Progress</Label>
                  <span className="text-emerald-600 font-bold">{newProgress.recovery_percent}%</span>
                </div>
                <Slider 
                  value={[newProgress.recovery_percent]}
                  onValueChange={([v]) => setNewProgress({...newProgress, recovery_percent: v})}
                  max={100}
                  step={5}
                />
              </div>
              <div>
                <div className="flex justify-between mb-2">
                  <Label>Pain Level (1 = no pain, 10 = severe)</Label>
                  <span className="text-red-600 font-bold">{newProgress.pain_level}/10</span>
                </div>
                <Slider 
                  value={[newProgress.pain_level]}
                  onValueChange={([v]) => setNewProgress({...newProgress, pain_level: v})}
                  min={1}
                  max={10}
                  step={1}
                />
              </div>
              <div>
                <div className="flex justify-between mb-2">
                  <Label>Mobility Rating</Label>
                  <span className="text-blue-600 font-bold">{newProgress.mobility_rating}/10</span>
                </div>
                <Slider 
                  value={[newProgress.mobility_rating]}
                  onValueChange={([v]) => setNewProgress({...newProgress, mobility_rating: v})}
                  min={1}
                  max={10}
                  step={1}
                />
              </div>
              <div>
                <div className="flex justify-between mb-2">
                  <Label>Fitness Readiness</Label>
                  <span className="text-purple-600 font-bold">{newProgress.fitness_readiness}/10</span>
                </div>
                <Slider 
                  value={[newProgress.fitness_readiness]}
                  onValueChange={([v]) => setNewProgress({...newProgress, fitness_readiness: v})}
                  min={1}
                  max={10}
                  step={1}
                />
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea 
                  value={newProgress.notes}
                  onChange={(e) => setNewProgress({...newProgress, notes: e.target.value})}
                  placeholder="Observations, concerns, improvements..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setProgressDialog(false)}>Cancel</Button>
              <Button onClick={handleAddProgress} className="bg-pfap-green hover:bg-pfap-green/90">
                Record Progress
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Clearance Dialog */}
        <Dialog open={clearanceDialog} onOpenChange={setClearanceDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Return-to-Play Clearance</DialogTitle>
              <DialogDescription>Update clearance status for this injury</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Clearance Status</Label>
                <Select 
                  value={newClearance.clearance_status} 
                  onValueChange={(v) => setNewClearance({...newClearance, clearance_status: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Not Cleared">Not Cleared</SelectItem>
                    <SelectItem value="Cleared">Cleared to Play</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Restrictions (if any)</Label>
                <Textarea 
                  value={newClearance.restrictions}
                  onChange={(e) => setNewClearance({...newClearance, restrictions: e.target.value})}
                  placeholder="e.g., Light training only for 1 week"
                />
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea 
                  value={newClearance.notes}
                  onChange={(e) => setNewClearance({...newClearance, notes: e.target.value})}
                  placeholder="Additional notes..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setClearanceDialog(false)}>Cancel</Button>
              <Button 
                onClick={handleClearance} 
                className={newClearance.clearance_status === 'Cleared' ? 'bg-pfap-green hover:bg-pfap-green/90' : 'bg-amber-600 hover:bg-amber-700'}
              >
                {newClearance.clearance_status === 'Cleared' ? 'Clear to Play' : 'Update Status'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

// Injury History Component
const InjuryHistory = ({ athleteId, currentInjuryId }) => {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (athleteId) {
      fetchHistory();
    }
  }, [athleteId]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/injury-recovery/history/athlete/${athleteId}`);
      setHistory(response.data);
    } catch (error) {
      console.error('Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
      </div>
    );
  }

  if (!history || history.history?.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-gray-500 dark:text-gray-500">
          No injury history records
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">{history.stats?.total_injuries || 0}</p>
            <p className="text-sm text-gray-500 dark:text-gray-500">Total Injuries</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold text-emerald-600">{history.stats?.recovered || 0}</p>
            <p className="text-sm text-gray-500 dark:text-gray-500">Recovered</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold text-red-600">{history.stats?.active || 0}</p>
            <p className="text-sm text-gray-500 dark:text-gray-500">Active</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold text-blue-600">
              {history.stats?.average_recovery_days ? Math.round(history.stats.average_recovery_days) : '-'}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500">Avg Recovery Days</p>
          </CardContent>
        </Card>
      </div>

      {/* History List */}
      <Card>
        <CardHeader>
          <CardTitle>Injury History</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {history.history?.map(item => (
              <div 
                key={item.injury.id}
                className={`p-4 rounded-lg border ${
                  item.injury.id === currentInjuryId 
                    ? 'border-red-300 bg-red-50' 
                    : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900'
                }`}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold">{item.injury.injury_type}</span>
                      <Badge className={item.outcome === 'Recovered' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}>
                        {item.outcome}
                      </Badge>
                      <Badge variant="outline">{item.injury.severity}</Badge>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-500">
                      {item.injury.body_area} • {new Date(item.injury.date_of_injury).toLocaleDateString()}
                      {item.duration_days && ` • ${item.duration_days} days`}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500 dark:text-gray-500">{item.progress_updates} progress updates</p>
                    {item.clearance && (
                      <Badge className={item.clearance.clearance_status === 'Cleared' ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}>
                        {item.clearance.clearance_status}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default InjuryRecoveryPage;
